<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Brand logo-->
      <b-link class="brand-logo d-flex align-items-center">
        <vuexy-logo />
        <h2
          v-if="!appConfig.logo_only"
          class="brand-text text-primary ml-1 mb-0"
        >
          {{ appConfig.brand }}
        </h2>
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        xl="8"
        lg="6"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img
            fluid
            :src="imgUrl"
            alt="Login V2"
          />
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        xl="4"
        lg="6"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title
            class="mb-1 font-weight-bold"
            title-tag="h2"
          >
            {{ $t("Welcome to brand",{brand:appConfig.brand}) }} 👋
          </b-card-title>
          <!-- form -->
          <validation-observer
            ref="loginForm"
            #default="{ invalid }"
          >
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login"
            >
              <b-form-group
                :label="$t('Mobile')"
                label-for="login-mobile"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Mobile')"
                  vid="mobile"
                  rules="required"
                >
                  <vue-phone-number-input
                    v-model="fullMobile"
                    style="direction:ltr"
                    default-country-code="IR"
                    name="mobile"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('Mobile')"
                    :translations="{
                      countrySelectorLabel: $t('Country Code'),
                      countrySelectorError: $t('Choose a country'),
                      phoneNumberLabel: $t('Mobile'),
                      example: $t('Example')+' :'
                    }"
                    :dark="$store.state.appConfig.layout.skin === 'dark'"
                    dark-color="#283046"
                    @update="updateMobil"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <!-- forgot password -->
              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="login-password">{{ $t("Password") }}</label>
                  <b-link :to="{ name: 'auth-forgot-password' }">
                    <small>{{ $t("Forgot Password") }}</small>
                  </b-link>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name=" $t('Password')"
                  vid="password"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="password"
                      :state="errors.length > 0 ? false : null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      :placeholder="$t('Password')"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- checkbox -->
              <b-form-group>
                <b-form-checkbox
                  id="remember-me"
                  v-model="remember"
                  name="checkbox-1"
                >
                  {{ $t("Remember Me") }}
                </b-form-checkbox>
              </b-form-group>

              <!-- submit buttons -->
              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="invalid || loader"
              >
                {{ $t("Sign in") }}
                <b-spinner
                  v-if="loader"
                  small
                />
              </b-button>
            </b-form>
          </validation-observer>

          <b-card-text class="text-center mt-2">
            <span>{{ $t("You do not have an account") }} </span>
            <b-link :to="{ name: 'auth-register' }">
              <span>&nbsp;{{ $t("You must register") }}</span>
            </b-link>
          </b-card-text>
        </b-col>
      </b-col>
      <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BFormCheckbox,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import useJwt from '@/auth/jwt/useJwt'
import store from '@/store/index'
import { getHomeRouteForLoggedInUser, permissionsResolver } from '@/auth/utils'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BSpinner,
    VuexyLogo,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      loader: false,
      remember: false,
      mobile: '',
      password: '',
      sideImg: require('@/assets/images/pages/auth-v1.svg'),
      fullMobile: '',
      validMobile: false,
      // validation rules
      required,
    }
  },
  computed: {
    appConfig() {
      return store.state.app.appConfig
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/auth-v1-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  methods: {
    updateMobil(e) {
      this.validMobile = e.isValid
      if (e.isValid) this.mobile = e.e164
    },
    login() {
      this.$refs.loginForm.validate().then(success => {
        if (success && this.validMobile) {
          this.loader = true
          this.$http
            .post('/login', {
              username: this.mobile,
              mobile: this.mobile,
              password: this.password,
              rememberMe: this.remember,
            })
            .then(({ data }) => {
              const {
                user,
              } = data
              useJwt.setToken(data.access_token)
              useJwt.setRefreshToken(data.refresh_token)
              localStorage.setItem('userData', JSON.stringify(user))
              const permissions = permissionsResolver(user.permissions)
              permissions.push([
                {
                  action: 'manage',
                  subject: 'all',
                },
              ])
              this.$ability.update(permissions)
              this.$router
                .replace(getHomeRouteForLoggedInUser(user.role))
                .then(() => {
                  this.showSuccessNotification(`${this.$t('Welcome')} ${user.name}`)
                })
            })
            .catch(() => {
              this.loader = false
            })
        } else {
          this.showErrorNotification(this.$t('check form data'))
          this.loader = false
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/vue/pages/page-auth.scss";
</style>
